import React from "react";
import NestedMenu from "../Shared/NestedMenu";
import { useAuth } from "hooks/useAuth";

const CRMSidebar = () => {
  const { isAccountAdmin, isSystemAdmin, isEntityAdmin, isEntityManager } = useAuth();
  const isEntityUser = !isAccountAdmin && !isSystemAdmin && !isEntityAdmin;

  const ACCOUNT_LVL_ADMINISTRATIVE_MENUS = [
    {
      title: "Account List",
      path: "/app/crm/account-list",
      icon: "ManageAccountsOutlined",
    },
    {
      title: "Global List",
      path: "/app/crm/global-list",
      icon: "LanguageOutlined",
    },
    {
      title: "Countries",
      path: "/app/crm/country/list",
      icon: "FlagOutlined",
    },
    {
      title: "Authorizations",
      path: "/app/crm/authorization/list/account",
      icon: "LockOutlined",
    },
    ...(isSystemAdmin
      ? [
        {
          title: "Accounts",
          path: "/app/crm/account/list",
          icon: "SupervisedUserCircleOutlined",
        },
      ]
      : []),
    {
      title: "Entities",
      path: "/app/crm/entity/list",
      icon: "InfoOutlined",
    },
  ];

  const SYSTEM_LVL_ADMINISTRATION_MENUS = [
    {
      title: "Email Templates",
      path: "/app/crm/email-template/list",
      icon: "AlternateEmailOutlined",
    },
    {
      title: "Accounts",
      path: "/app/crm/account/list",
      icon: "GroupOutlined",
    },
  ];

  const ADMINISTRATION_MENUS = [
    {
      title: "Administration",
      icon: "AdminPanelSettingsOutlined",
      subMenus: [
        {
          title: "Blogs",
          path: "/app/crm/blog/list",
          icon: "ArticleOutlined",
        },
        {
          title: "Teams",
          path: "/app/crm/team/list",
          icon: "GroupsOutlined",
        },
        {
          title: "Entity List",
          path: "/app/crm/entity-list",
          icon: "List",
        },
        ...(isAccountAdmin || isSystemAdmin
          ? ACCOUNT_LVL_ADMINISTRATIVE_MENUS
          : []),
        {
          title: "Users",
          path: "/app/crm/user/list",
          icon: "AdminPanelSettingsOutlined",
        },
        ...(isAccountAdmin
          ? [
            {
              title: "Account Info",
              path: "/app/crm/account/info",
              icon: "GroupOutlined",
            },
          ]
          : []),
        ...(isSystemAdmin ? SYSTEM_LVL_ADMINISTRATION_MENUS : []),
      ],
    },
  ];

  const SHIPMENT_MENU = [
    {
      title: "Shipment",
      icon: "GroupWorkOutlined",
      subMenus: [
        {
          title: "Shipment",
          path: "/app/crm/shipment/list",
          icon: "LocalShippingOutlined",
        },
        {
          title: "Collection",
          path: "/app/crm/collection/list",
          icon: "CategoryOutlined",
        },
        {
          title: "Consolidation",
          path: "/app/crm/consolidation/list",
          icon: "JoinFullOutlined",
        },
        {
          title: "Loading",
          path: "/app/crm/shipment-loading/list",
          icon: "LocalShipping",
        },
        {
          title: "Unloading",
          path: "/app/crm/shipment-unLoading/list",
          icon: "LocalShippingOutlined",
        },
        {
          title: "Deconsolidation",
          path: "/app/crm/deconsolidation/list",
          icon: "JoinFullOutlined",
        },
        {
          title: "Cross-Docking",
          path: "/app/crm/cross-docking/list",
          icon: "DockOutlined",
          inDevelopment: true,
        },
        {
          title: "Warehousing",
          path: "/app/crm/warehousing/list",
          icon: "FactoryOutlined",
        },
        {
          title: "Send Out",
          path: "/app/crm/sendOut/list",
          icon: "OutboxOutlined",
        },
        {
          title: "Delivery",
          path: "/app/crm/delivery/list",
          icon: "LocalShippingOutlined",
        },
        {
          title: "Send Out Collection",
          path: "/app/crm/sendOutCollection/list",
          icon: "PlaylistAddOutlined",
        },
        // {
        //   title: "Document",
        //   icon: "AssignmentOutlined",
        //   path: "/app/crm/collection-document/list",
        // },
      ],
    },
  ];

  const TRANSPORT_MENU = [
    {
      title: "Transport",
      icon: "DriveEtaOutlined",
      subMenus: [
        {
          title: "Driver",
          path: "/app/crm/driver/list",
          icon: "ManageAccountsOutlined",
        },
        {
          title: "Vehicle",
          path: "/app/crm/vehicle/list",
          icon: "DriveEtaOutlined",
        },
        {
          title: "Vehicle Hiring",
          path: "/app/crm/vehicle-hiring-entry/list",
          icon: "DepartureBoard",
        },
        {
          title: "Vehicle Maintenance",
          path: "/app/crm/vehicle-maintenance/list",
          icon: "DriveEtaOutlined",
        },
        {
          title: "Fuel Record",
          path: "/app/crm/vehicle-fuel/list",
          icon: "EvStationOutlined",
        },
        {
          title: "Merchants",
          path: "/app/crm/merchant/list",
          icon: "FmdGoodOutlined",
        },
        {
          title: "Trips",
          path: "/app/crm/trip/list",
          icon: "LocalShippingOutlined",
          inDevelopment: true,
        },
        {
          title: "Incident Report",
          path: "/app/crm/incident-report/list",
          icon: "FeedOutlined",
        },
        {
          title: "Real Time Tracking",
          path: "/app/crm/realtime-tracking/list",
          icon: "MyLocationOutlined",
        },
        {
          title: "Load Transfer",
          path: "/app/crm/load-transfer/list",
          icon: "LocalShippingOutlined",
        },
        {
          title: "Local Transport Request",
          path: "/app/crm/local-transport/list",
          icon: "DirectionsBikeOutlined",
        },
        ...(isAccountAdmin
          ? [
            {
              title: "Local Transport Rules",
              path: "/app/crm/local-transport-rule/list",
              icon: "RuleOutlined",
            },
          ]
          : []),
      ],
    },
  ];

  const CONTACT_MENU = [
    {
      title: "Contact",
      icon: "ManageAccountsOutlined",
      subMenus: [
        {
          title: "Customer",
          path: "/app/crm/customerUser/list",
          icon: "AdminPanelSettingsOutlined",
        },
        {
          title: "Customer Profile",
          path: "/app/crm/customerProfile/list",
          icon: "PeopleAltOutlined",
        },
        {
          title: "Vendor",
          path: "/app/crm/vendor/list",
          icon: "LocalShipping",
        },
        {
          title: "Vendor Profile",
          path: "/app/crm/vendor-profile/list",
          icon: "Storefront",
        },
        {
          title: "Contractor",
          path: "/app/crm/contractorUser/list",
          icon: "ManageAccountsOutlined",
        },
        {
          title: "Contractor Profile",
          path: "/app/crm/contractor/list",
          icon: "ManageAccountsOutlined",
        },
      ],
    },
  ];

  const PAYMENT_MENUS = [
    {
      title: "Payments",
      icon: "PaymentsOutlined",
      subMenus: [
        {
          title: "General Ledger",
          path: "/app/crm/general-ledger/list",
          icon: "AccountBalanceWalletOutlined",
        },
        {
          title: "Payment Account",
          path: "/app/crm/payment-account/list",
          icon: "AccountBalanceWalletOutlined",
          inDevelopment: true,
        },
        {
          title: "Account Ledger",
          path: "/app/crm/account-ledger/list",
          icon: "AssignmentOutlined",
          inDevelopment: true,
        },
        {
          title: "Counter Collection",
          path: "/app/crm/coc-collection/list",
          icon: "AccountBalanceOutlined",
        },
        {
          title: "Delivery Collection",
          path: "/app/crm/delivery-collection/list",
          icon: "PaymentOutlined",
        },
        {
          title: "COD Transfer",
          path: "/app/crm/cod-transfer/list",
          icon: "SwapHorizOutlined",
        },
        {
          title: "Shipment Refund",
          path: "/app/crm/shipment-refund/list",
          icon: "AccountBalanceOutlined",
        },

        {
          title: "Prepayment",
          path: "/app/crm/prepayment/list",
          icon: "RotateRightOutlined",
        },
        {
          title: "Adjustment",
          path: "/app/crm/adjustment/list",
          icon: "BuildCircleOutlined",
          inDevelopment: true,
        },
      ],
    },
  ];

  // const OperationsCompliance_MENU = [
  //   {
  //     title: "Operations",
  //     icon: "PeopleAltOutlined",
  //     subMenus: [
  //       {
  //         title: "Compliance",
  //         path: "/app/crm/compliance/list",
  //         icon: "TipsAndUpdatesOutlined",
  //       },
  //       {
  //         title: "Data Integration",
  //         path: "/app/crm/dataIntegration/list",
  //         icon: "IntegrationInstructionsOutlined",
  //       },
  //               {
  //         title: "Transshipment",
  //         path: "/app/crm/transshipment/list",
  //         icon: "PeopleAltOutlined",
  //       },
  //       {
  //         title: "Communication",
  //         path: "/app/crm/communication/list",
  //         icon: "NfcOutlined",
  //       },
  //     ],
  //   },
  // ];
  const BRANCHES_MENUS = [
    {
      title: "Locations",
      icon: "LocationOnOutlined",
      subMenus: [
        {
          title: "Branch Tally",
          path: "/app/crm/branch-tally/list",
          icon: "StorefrontOutlined",
        },
        {
          title: "Province",
          path: "/app/crm/region/list",
          icon: "PublicOutlined",
        },
        {
          title: "District",
          path: "/app/crm/district/list",
          icon: "LocationCity",
        },
        {
          title: "Municipality",
          path: "/app/crm/municipality/list",
          icon: "ApartmentOutlined",
        },
        {
          title: "Branch",
          path: "/app/crm/branch/list",
          icon: "StorefrontOutlined",
        },

        {
          title: "Branch Assignment",
          path: "/app/crm/branchAssignment/list",
          icon: "AssignmentTurnedInOutlined",
        },

        {
          title: "Warehouse",
          path: "/app/crm/warehouse/list",
          icon: "WarehouseOutlined",
        },
        {
          title: "Route",
          path: "/app/crm/route-shipment/list",
          icon: "AltRouteOutlined",
        },
        // {
        //   title: "Legs",
        //   path: "/app/crm/leg/list",
        //   icon: "AltRouteOutlined",
        // },
      ],
    },
  ];

  const MY_REPORTS = [
    {
      title: "My Reports",
      icon: "AssessmentOutlined",
      subMenus: [
        {
          title: "COC Collection",
          path: "/app/crm/reports/my-coc-collection-report",
          icon: "AccountBalanceOutlined",
        },

        {
          title: "COD Collection",
          path: "/app/crm/reports/my-cod-collection-report",
          icon: "SummarizeOutlined",
        },
      ],
    },
  ];

  const VendorLead = [
    {
      title: "Vendor Lead",
      icon: "AssessmentOutlined",
      subMenus: [
        {
          title: "Vendor Lead",
          path: "/app/crm/vendor-lead/list",
          icon: "GroupOutlined",
        },

        {
          title: "Vendor Quotation PDF",
          path: "/app/crm/vendor-quotation/list",
          icon: "SummarizeOutlined",
        },
        {
          title: "Vendor Quotation Excel",
          path: "/app/crm/vendor-quotation-excel/list",
          icon: "SummarizeOutlined",
        },
        {
          title: "Vendor Profile Quotation List",
          path: "/app/crm/vendor-lead/quotation-list-vendor",
          icon: "SummarizeOutlined",
        },
      ],
    },
  ];

  const adminMenuOptions = [
    {
      title: "Dashboard",
      path: "/app/crm/dashboard",
      icon: "DashboardOutlined",
    },
    ...SHIPMENT_MENU,
    ...TRANSPORT_MENU,
    ...CONTACT_MENU,
    ...BRANCHES_MENUS,
    ...MY_REPORTS,
    ...VendorLead,
    ...(isEntityUser
      ? [
        {
          title: "Payments",
          icon: "Payments",
          subMenus: [
            {
              title: "Counter Collection",
              path: "/app/crm/coc-collection/list",
              icon: "AccountBalanceOutlined",
            },
            {
              title: "Delivery Collection",
              path: "/app/crm/delivery-collection/list",
              icon: "PaymentOutlined",
            },
          ],
        },
        {
          title: "Report",
          icon: "SummarizeSharpOutlined",
          path: "/app/crm/reports/list",
        },
        ...isEntityManager ? [{
          title: "User",
          icon: "SupervisedUserCircleOutlined",
          path: "/app/crm/user/list",
        }] : [],
      ]
      : [
        ...PAYMENT_MENUS,
        ...ADMINISTRATION_MENUS,
        {
          title: "Setting",
          icon: "SettingsOutlined",
          path: "/app/crm/setting/list",
        },
        {
          title: "Announcement",
          icon: "CampaignOutlined",
          path: "/app/crm/announcement/list",
        },
        {
          title: "Report",
          icon: "SummarizeOutlined",
          path: "/app/crm/reports/list",
        },
        {
          title: "Tasks",
          icon: "AddTask",
          path: "/app/crm/task/list",
        },
        {
          title: "Tickets",
          icon: "ConfirmationNumberOutlined",
          path: "/app/crm/ticket/list",
        },
      ]),
  ];

  const GLOBAL = [
    {
      title: "Feedbacks",
      icon: "FeedbackOutlined",
      path: "/app/crm/feedback/list",
    },
  ];

  return (
    <NestedMenu
      menu={[...adminMenuOptions, ...GLOBAL]}
      initiallyOpenedMenu="Sales"
    />
  );
};

export default CRMSidebar;
